import { Avatar, Center, Text } from '@mantine/core'

import { calculateBrightness } from '../utils/color'
import PlayerHeadshot from './PlayerHeadshot'

interface CompetitorLogoProps {
  /** Render size in px. TwoWayCard outcome row 用 40。 */
  size: number
  /** 选手头像/国旗源（仅 isPlayerMatch 且 BE 给了 headshot/flag url 时）。 */
  playerImage?: { src?: string; isFlag: boolean }
  /** Team primary color (hex)。无 logo 时填充 fallback badge 并据此选亮/暗字。 */
  primaryColor?: string
  /** Team / event logo URL。最高优先级；加载失败回退到 badge。 */
  logoUrl?: string
  /** 无 logo 无头像时的 initials 文本；两者都缺则整个槽位省略。 */
  fallbackText?: string
  /** Corner-radius override (px)。默认按 size 分档（≤16→3, ≤32→8, ≤44→12, 更大→0.25×size）。 */
  radius?: number
}

/**
 * Port of cdc-web `src/components/knockOuts/CompetitorLogo.tsx`。
 * 裁剪：去掉 TwoWayCard 用不到的 `secondaryColor` / `isNational` /
 * `drawPlaceholder`（含 DrawBadge）——TwoWayCard 不渲染 Draw 行（OG-9944，
 * 3 路赛事也只显示两行）。其余（squareRadius/fallbackFontSize/FallbackBadge
 * 亮度对比选字）1:1 保留。
 *
 * Precedence：
 *   1. `logoUrl`         → image（圆角方块）；error → fallback badge
 *   2. `playerImage.src` → 选手头像；error → 线稿人形
 *   3. `fallbackText`    → 实心 badge + initials（队色或灰）
 *   4. 都无              → 省略
 */

/** Brightness midpoint (0–255) of the (299·R+587·G+114·B)/1000 formula. */
const CONTRAST_MIDPOINT = 128
/** Muted fill for the badge when no team color is available. */
const MUTED_FALLBACK_BG = '#3a3a3a'

/** Rounded-square corner radius scaled to the badge size, matching og-app. */
function squareRadius(size: number): number {
  if (size <= 16) return 3
  if (size <= 32) return 8
  if (size <= 44) return 12
  return Math.round(size * 0.25)
}

/** Fallback-badge initials font size, scaled to the badge size. */
function fallbackFontSize(size: number): string {
  if (size <= 16) return '5px'
  if (size <= 20) return '6px'
  if (size <= 32) return '11px'
  if (size <= 44) return '13px'
  return `${Math.round(size * 0.32)}px`
}

export function CompetitorLogo({
  size,
  playerImage,
  primaryColor,
  logoUrl,
  fallbackText,
  radius,
}: Readonly<CompetitorLogoProps>) {
  const resolvedRadius = radius ?? squareRadius(size)
  const fallback = fallbackText ? (
    <FallbackBadge
      size={size}
      text={fallbackText}
      primaryColor={primaryColor}
      radius={resolvedRadius}
    />
  ) : null

  // Tier 1: real logo asset. On image error, Avatar renders the fallback.
  if (logoUrl) {
    return (
      <Avatar
        src={logoUrl}
        size={size}
        radius={resolvedRadius}
        alt=""
        style={{ '--avatar-bg': 'none' }}
      >
        {fallback}
      </Avatar>
    )
  }

  // Tier 2: player headshot — rounded square, generic person on load error.
  if (playerImage?.src) {
    return (
      <PlayerHeadshot
        src={playerImage.src}
        size={size}
        radius={resolvedRadius}
        showFallbackOnError
      />
    )
  }

  // Tier 3: terminal fallback (initials badge); null when neither applies.
  return fallback
}

/**
 * No-logo fallback tile (OG-8486 AC1/AC2): a rounded square filled with the
 * team primary color and the entity's initials. Initials flip dark/light by
 * the fill's luminance so they stay legible (AC1). With no color it falls to a
 * muted grey fill with light initials (players / countries, AC2).
 */
function FallbackBadge({
  size,
  text,
  primaryColor,
  radius,
}: {
  size: number
  text: string
  primaryColor?: string
  radius?: number
}) {
  const hasColor = !!primaryColor
  const bg = hasColor ? primaryColor : MUTED_FALLBACK_BG
  // cdc-web 这边用 `var(--common-dark)` / `var(--common-white)`，但 cdc-market 的
  // token 生成器把 `common.dark` 当主题作用域后缀剥离，只产出残缺的 `--common-`，
  // `--common-dark` 全仓未定义（devtools 报 not defined）。徽章字是对比兜底，
  // 色值固定，直接写字面值对齐 cdc-web 的渲染（--common-dark = --grey-900 =
  // #0b1426，--common-white = #ffffff）。
  const textColor =
    hasColor && calculateBrightness(primaryColor) > CONTRAST_MIDPOINT ? '#0b1426' : '#ffffff'

  return (
    <Center
      w={size}
      h={size}
      bg={bg}
      style={{ borderRadius: radius ?? squareRadius(size), flexShrink: 0 }}
    >
      <Text
        w="100%"
        fz={fallbackFontSize(size)}
        fw={700}
        c={textColor}
        ta="center"
        lh="normal"
        style={{ paddingInline: 1 }}
        truncate
      >
        {text}
      </Text>
    </Center>
  )
}
